import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, Shield, Upload, RefreshCcw, BarChart3, Film, Tv, Users, Clock, Star, X, Radio, Award, User2, Check, Ban, Search as SearchIcon, KeyRound, Mail, Crown, Clapperboard, Bell, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/* ---------------- SnacksAdmin: curate the cinema food & drinks menu ---------------- */
const PROVIDERS = [
  { v: "kitchen",   l: "Cinema Kitchen" },
  { v: "zomato",    l: "Zomato" },
  { v: "noon",      l: "Noon Food" },
  { v: "paanidost", l: "Paanidost" },
];
const CATEGORIES = ["snack", "drink", "combo"];

export default function SnacksAdmin() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  // New item form state
  const [n, setN] = useState({ name: "", price: "", category: "snack", provider: "kitchen", image_url: "", description: "" });

  const load = () => {
    api.get("/admin/cinema/snacks").then((r) => setItems(r.data?.snacks || [])).catch(() => setItems([]));
    api.get("/admin/cinema/snacks/orders?limit=20").then((r) => setOrders(r.data?.orders || [])).catch(() => setOrders([]));
  };
  useEffect(load, []);

  const create = async () => {
    if (!n.name.trim() || !n.price) { toast.error("Name + price required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/cinema/snacks", { ...n, price: parseFloat(n.price) });
      toast.success(`Added "${n.name}"`);
      setN({ name: "", price: "", category: "snack", provider: "kitchen", image_url: "", description: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (it) => {
    try {
      await api.patch(`/admin/cinema/snacks/${it.item_id}`, { active: !it.active });
      toast.success(it.active ? "Disabled" : "Enabled");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}" permanently?`)) return;
    try {
      await api.delete(`/admin/cinema/snacks/${it.item_id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const updatePrice = async (it, newPrice) => {
    const p = parseFloat(newPrice);
    if (!p || p <= 0) return;
    try {
      await api.patch(`/admin/cinema/snacks/${it.item_id}`, { price: p });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold mb-1 flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-amber-400" /> Snacks Menu
        </h2>
        <p className="text-sm text-gray-400 mb-4">
          Curate the food & drinks lobby visitors order from on <code className="text-gray-300">/screens</code>. Each item routes to its provider on checkout.
        </p>
      </div>

      {/* New item form */}
      <div data-testid="snacks-admin-new" className="bg-[#0f0f0f] border border-white/10 rounded-lg p-4 space-y-2">
        <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-2">+ Add menu item</h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <input value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} placeholder="Name (Salted Popcorn)" className="md:col-span-2 bg-black/40 border border-white/15 rounded px-2 py-1.5 text-sm" data-testid="snack-new-name" />
          <input value={n.price} onChange={(e) => setN({ ...n, price: e.target.value })} type="number" step="0.50" placeholder="Price" className="bg-black/40 border border-white/15 rounded px-2 py-1.5 text-sm" data-testid="snack-new-price" />
          <select value={n.category} onChange={(e) => setN({ ...n, category: e.target.value })} className="bg-black/40 border border-white/15 rounded px-2 py-1.5 text-sm">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={n.provider} onChange={(e) => setN({ ...n, provider: e.target.value })} className="bg-black/40 border border-white/15 rounded px-2 py-1.5 text-sm" data-testid="snack-new-provider">
            {PROVIDERS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
          <button onClick={create} disabled={busy} data-testid="snack-new-save" className="bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm rounded px-3 py-1.5 disabled:opacity-50">
            {busy ? "…" : "Add"}
          </button>
        </div>
        <input value={n.image_url} onChange={(e) => setN({ ...n, image_url: e.target.value })} placeholder="Image URL (optional)" className="w-full bg-black/40 border border-white/15 rounded px-2 py-1.5 text-xs font-mono" />
      </div>

      {/* Menu list */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-3">Active menu · {items.length} items</h3>
        {items.length === 0 ? (
          <div className="text-sm text-gray-500 italic">No items yet. Add one above.</div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.item_id} data-testid={`snack-row-${it.item_id}`} className="bg-[#0f0f0f] border border-white/5 rounded-lg p-3 flex items-center gap-3">
                {it.image_url ? <img src={it.image_url} alt={it.name} className="w-12 h-12 rounded object-cover" /> : <div className="w-12 h-12 rounded bg-white/5" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{it.name}</div>
                  <div className="text-[11px] text-gray-500">
                    {it.category} · via {PROVIDERS.find((p) => p.v === it.provider)?.l || it.provider}
                  </div>
                </div>
                <input
                  defaultValue={it.price}
                  onBlur={(e) => e.target.value != it.price && updatePrice(it, e.target.value)}
                  type="number"
                  step="0.50"
                  className="w-20 bg-black/40 border border-white/15 rounded px-2 py-1 text-sm text-right"
                />
                <button onClick={() => toggle(it)} className={`text-xs px-3 py-1.5 rounded border ${it.active ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" : "border-gray-500/30 text-gray-400 hover:bg-white/5"}`}>
                  {it.active ? "Active" : "Disabled"}
                </button>
                <button onClick={() => remove(it)} data-testid={`snack-delete-${it.item_id}`} className="text-red-400 hover:text-red-300 p-1.5">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent orders */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-3">Recent orders</h3>
        {orders.length === 0 ? (
          <div className="text-sm text-gray-500 italic">No orders yet.</div>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.order_id} className="bg-[#0f0f0f] border border-white/5 rounded-lg p-3 text-sm flex items-center gap-3">
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${
                  o.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" :
                  "bg-gray-500/15 text-gray-300 border-gray-500/40"
                }`}>{o.payment_status}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{o.user_email || o.user_id} · ${o.total?.toFixed(2)}</div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {o.lines?.length || 0} item{o.lines?.length === 1 ? "" : "s"}
                    {o.screen_no ? ` · Screen ${o.screen_no}` : ""}
                    {o.seat ? ` · Seat ${o.seat}` : ""}
                  </div>
                </div>
                <span className="text-[10px] text-gray-500">{new Date(o.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
